import { times, random } from 'lodash';

export function generateRandomString(length) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return times(length, () => characters[random(characters.length - 1)]).join('');
}

/**
 * Function to determine if an element is visible in the DOM.
 * This method checks the element's styles (display, visibility, opacity)
 * and ensures that both the element and its parent elements are visible.
 *
 * @param {HTMLElement} element - The target DOM element to check.
 * @returns {boolean} - Returns true if the element is visible, otherwise false.
 */
export function isVisible(element) {
  if (!element) return false; // If the element does not exist, return false.

  /**
   * Helper function to check if an individual element is visible.
   * It considers styles like display, visibility, opacity, and element dimensions.
   *
   * @param {HTMLElement} el - The DOM element to check.
   * @returns {boolean} - Returns true if the element itself is visible.
   */
  function checkSelf(el) {
    const style = window.getComputedStyle(el); // Get the computed styles of the element.
    return (
      style.display !== 'none' && // Element is not set to "display: none".
      style.visibility !== 'hidden' && // Element is not set to "visibility: hidden".
      style.opacity !== '0' && // Element is not fully transparent.
      (el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0) // Element has visible dimensions.
    );
  }

  let current = element;

  // Traverse up the DOM tree to check parent elements.
  while (current) {
    if (!checkSelf(current)) {
      return false; // If any parent is not visible, return false.
    }
    current = current.parentElement; // Move up to the parent element.
  }

  return true; // If all checks pass, the element is visible.
}

export const cachePromise = (func, config = { isPersisted: true }) => {
  let m = new Map();
  return async function (...args) {
    const params = args.map(a => a instanceof FormData ? Object.fromEntries(a) : a);
    const key = JSON.stringify(params);

    if (!m.has(key)) {
      const p = async () => {
        const res = await func(...args);
        if (!config.isPersisted) {
          m.delete(key);
        }
        return res;
      }
      m.set(key, p());
    }
    return m.get(key);
  }
}